import { useId, CSSProperties } from 'react'

interface ErlenmeyerFlaskProps {
  size?: 125 | 250 | 500
  fillLevel?: number // 0-100
  fillColor?: string
  hasStirBar?: boolean
  stirring?: boolean
  label?: string
  active?: boolean
  onClick?: () => void
  disabled?: boolean
  className?: string
}

const ErlenmeyerFlask = ({
  size = 250,
  fillLevel = 0,
  fillColor = '#7DD3FC',
  hasStirBar = false,
  stirring = false,
  label = '',
  active = false,
  onClick,
  disabled = false,
  className = ''
}: ErlenmeyerFlaskProps) => {
  const id = useId()

  // Size scaling
  const scale = size === 125 ? 0.7 : size === 500 ? 1.2 : 1
  const width = Math.round(100 * scale)
  const height = Math.round(140 * scale)

  // Flask dimensions (conical shape)
  const neckTop = 15
  const neckBottom = 40
  const neckWidth = 14
  const bodyTop = neckBottom
  const bodyBottom = 120
  const bodyTopWidth = 20
  const bodyBottomWidth = 80
  const centerX = 50

  // Calculate fill height
  const innerHeight = bodyBottom - bodyTop - 10
  const fillHeight = (fillLevel / 100) * innerHeight
  const fillTop = bodyBottom - 5 - fillHeight

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
  }

  return (
    <svg
      viewBox="0 0 100 140"
      width={width}
      height={height}
      style={containerStyle}
      className={className}
      onClick={disabled ? undefined : onClick}
    >
      <defs>
        {/* Glass gradient */}
        <linearGradient id={`glass-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(170,200,220,0.8)" />
          <stop offset="10%" stopColor="rgba(220,240,255,0.5)" />
          <stop offset="20%" stopColor="rgba(255,255,255,0.3)" />
          <stop offset="50%" stopColor="rgba(200,230,245,0.15)" />
          <stop offset="80%" stopColor="rgba(255,255,255,0.25)" />
          <stop offset="90%" stopColor="rgba(220,240,255,0.4)" />
          <stop offset="100%" stopColor="rgba(170,200,220,0.7)" />
        </linearGradient>

        {/* Liquid gradient */}
        <linearGradient id={`liquid-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={fillColor} stopOpacity="0.85" />
          <stop offset="100%" stopColor={fillColor} stopOpacity="0.95" />
        </linearGradient>

        {/* Rim gradient */}
        <linearGradient id={`rim-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#A0C0D8" />
          <stop offset="50%" stopColor="#7A9BB8" />
          <stop offset="100%" stopColor="#5A7A95" />
        </linearGradient>

        {/* Shadow */}
        <filter id={`shadow-${id}`} x="-20%" y="-10%" width="140%" height="130%">
          <feDropShadow dx="3" dy="4" stdDeviation="3" floodOpacity="0.3"/>
        </filter>

        {/* Clip for liquid - conical shape */}
        <clipPath id={`liquid-clip-${id}`}>
          <path d={`
            M${centerX - bodyTopWidth/2 + 5} ${bodyTop + 5}
            L${centerX - bodyBottomWidth/2 + 5} ${bodyBottom - 5}
            L${centerX + bodyBottomWidth/2 - 5} ${bodyBottom - 5}
            L${centerX + bodyTopWidth/2 - 5} ${bodyTop + 5}
            Z
          `} />
        </clipPath>

        {/* Stir bar gradient */}
        <linearGradient id={`stirbar-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="50%" stopColor="#F5F5F5" />
          <stop offset="100%" stopColor="#E0E0E0" />
        </linearGradient>
      </defs>

      {/* Active glow */}
      {active && (
        <ellipse
          cx={centerX}
          cy={bodyBottom - 20}
          rx={bodyBottomWidth/2 + 8}
          ry={40}
          fill="none"
          stroke="var(--primary)"
          strokeWidth="3"
          style={{ animation: 'glow 1.5s ease-in-out infinite' }}
        />
      )}

      <g filter={`url(#shadow-${id})`}>
        {/* Flask body - conical shape */}
        <path
          d={`
            M${centerX - neckWidth/2} ${neckTop}
            L${centerX - neckWidth/2} ${neckBottom}
            L${centerX - bodyBottomWidth/2} ${bodyBottom - 10}
            Q${centerX - bodyBottomWidth/2} ${bodyBottom} ${centerX - bodyBottomWidth/2 + 10} ${bodyBottom}
            L${centerX + bodyBottomWidth/2 - 10} ${bodyBottom}
            Q${centerX + bodyBottomWidth/2} ${bodyBottom} ${centerX + bodyBottomWidth/2} ${bodyBottom - 10}
            L${centerX + neckWidth/2} ${neckBottom}
            L${centerX + neckWidth/2} ${neckTop}
          `}
          fill={`url(#glass-${id})`}
          stroke="#7A9BB8"
          strokeWidth="2"
        />

        {/* Neck rim */}
        <ellipse
          cx={centerX}
          cy={neckTop}
          rx={neckWidth/2 + 2}
          ry="3"
          fill={`url(#rim-${id})`}
          stroke="#6A8AA5"
          strokeWidth="1"
        />

        {/* Inner rim */}
        <ellipse
          cx={centerX}
          cy={neckTop}
          rx={neckWidth/2 - 1}
          ry="2"
          fill="rgba(50,80,100,0.3)"
        />

        {/* Bottom ellipse for 3D effect */}
        <ellipse
          cx={centerX}
          cy={bodyBottom - 3}
          rx={bodyBottomWidth/2 - 5}
          ry="4"
          fill="rgba(200,220,240,0.4)"
        />
      </g>

      {/* Liquid contents */}
      {fillLevel > 0 && (
        <g clipPath={`url(#liquid-clip-${id})`}>
          {/* Calculate liquid polygon based on fill level */}
          {(() => {
            const ratio = fillHeight / innerHeight
            const liquidBottomWidth = bodyBottomWidth - 10
            const widthAtFillTop = liquidBottomWidth - (liquidBottomWidth - bodyTopWidth + 10) * ratio
            return (
              <>
                {/* Main liquid body */}
                <path
                  d={`
                    M${centerX - widthAtFillTop/2} ${fillTop}
                    L${centerX - liquidBottomWidth/2} ${bodyBottom - 7}
                    L${centerX + liquidBottomWidth/2} ${bodyBottom - 7}
                    L${centerX + widthAtFillTop/2} ${fillTop}
                    Z
                  `}
                  fill={`url(#liquid-${id})`}
                />
                {/* Liquid surface (meniscus) */}
                <ellipse
                  cx={centerX}
                  cy={fillTop}
                  rx={widthAtFillTop/2 - 2}
                  ry="3"
                  fill={fillColor}
                  opacity="0.9"
                />
                {/* Surface highlight */}
                <ellipse
                  cx={centerX - widthAtFillTop/4}
                  cy={fillTop}
                  rx={widthAtFillTop/4}
                  ry="2"
                  fill="rgba(255,255,255,0.4)"
                />
              </>
            )
          })()}
        </g>
      )}

      {/* Glass highlights */}
      <path
        d={`M${centerX - neckWidth/2 + 2} ${neckTop + 5} L${centerX - bodyBottomWidth/2 + 8} ${bodyBottom - 15}`}
        stroke="rgba(255,255,255,0.5)"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />

      {/* Secondary highlight */}
      <path
        d={`M${centerX - neckWidth/2 + 4} ${neckTop + 8} L${centerX - bodyBottomWidth/2 + 15} ${bodyBottom - 20}`}
        stroke="rgba(255,255,255,0.25)"
        strokeWidth="1"
        strokeLinecap="round"
        fill="none"
      />

      {/* Magnetic stir bar */}
      {hasStirBar && (
        <g transform={`translate(${centerX}, ${bodyBottom - 12})`}>
          <g style={stirring ? { animation: 'spin 0.5s linear infinite' } : {}}>
            <rect
              x="-12"
              y="-3"
              width="24"
              height="6"
              rx="3"
              fill={`url(#stirbar-${id})`}
              stroke="#CCCCCC"
              strokeWidth="0.5"
            />
            {/* Stir bar end caps (PTFE coating look) */}
            <rect
              x="-12"
              y="-3"
              width="4"
              height="6"
              rx="2"
              fill="#E8E8E8"
            />
            <rect
              x="8"
              y="-3"
              width="4"
              height="6"
              rx="2"
              fill="#E8E8E8"
            />
          </g>
        </g>
      )}

      {/* Size label on flask */}
      <text
        x={centerX + bodyBottomWidth/2 - 15}
        y={bodyBottom - 30}
        fontSize="8"
        fill="#5A7A95"
        textAnchor="end"
        fontFamily="Arial, sans-serif"
      >
        {size} mL
      </text>

      {/* Label */}
      {label && (
        <text
          x={centerX}
          y="135"
          fontSize="10"
          fill="var(--text-primary, #E0E0E0)"
          textAnchor="middle"
          fontWeight="600"
        >
          {label}
        </text>
      )}

      {/* Spinning animation keyframes */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes glow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
      `}</style>
    </svg>
  )
}

export default ErlenmeyerFlask
